//Import
import React, { useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import Select, { components } from "react-select";
//Imports Context
import { AppContext } from "../../../contexts/AppContext";
import { AuthContext } from "../../../contexts/AuthContext";
import { DepartmentContext } from "../../../contexts/DepartmentContext";
import staticData from '../../../contexts/staticData.json';
//Import Components
import ViewPendingRequests from "../../viewPendingRequests/ViewPendingRequests";
//Import Helpers
import { formatDisplayName, getFullDepartmentName } from "../../../shared/helpers";
//Import Icons
import arrow from '../../../assets/icons/arrowSide.svg';
import downArrow from '../../../assets/icons/downArrow.svg';
//Import CSS
import './department.css';

function Department({ departmentName }) {
    const location = useLocation();
    const { usersList, dataProjects, requestsList, establishmentList, pmeList } = useContext(AppContext);
    const { dataDepartment, setDataDepartment, view, setView, viewObserved, setViewObserved, viewResponseObservation, setViewResponseObservation, selectedRequest, setSelectedRequest, currentRequestType, setCurrentRequestType } = useContext(DepartmentContext);
    const { userData } = useContext(AuthContext);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [pendingRequests, setPendingRequests] = useState([]);
    const [observedRequests, setObservedRequests] = useState([]);
    const [respondedObservations, setRespondedObservations] = useState([]);

    useEffect(() => {
        setDataDepartment(prevState => {
            if (prevState.department === departmentName) return prevState;
    
            return {
                ...prevState,
                department: departmentName
            };
        });
    }, [departmentName, setDataDepartment]);

    useEffect(() => {
        if (location.state) {
            setView(location.state.view || false);
            setViewObserved(location.state.viewObserved || false);
            setViewResponseObservation(location.state.viewResponseObservation || false);
            setSelectedRequest(location.state.selectedRequest || null);
            setCurrentRequestType(location.state.currentRequestType || 'purchase');
            setDataDepartment(location.state.data || {
                region: null,
                commune: null,
                holder: { value: null, id: null },
                department: departmentName
            });

            // Si venimos de PaymentRequestTracking, abrimos ViewPendingRequests
            if (location.state.viewPaymentOpen) {
                setView(true);
            }
        }
    }, [location.state, departmentName]);

    useEffect(() => {
        filterUsers();
        pendingRequestsSearch();
    }, [dataDepartment, userData, usersList, requestsList]);

    const filterUsers = () => {
        let filtered = [];

        if (userData?.permissions === 'admin' && dataDepartment?.holder.id) {
            filtered = usersList
                .filter((user) => user.tenantId === dataDepartment.holder.id)
                .filter((user) => user.department === dataDepartment?.department);
        } else if (userData?.dimension === 'Sostenedor' || userData?.dimension === 'Establecimiento') {
            filtered = usersList
                .filter((user) => user.tenantId === userData.tenantId)
                .filter((user) => user.department === dataDepartment.department);
        }
        setFilteredUsers(filtered);
    };

    const pendingRequestsSearch = () => {
        let pending = [];
        let observedRequests = [];
        let respondedObservations = [];

        if (userData?.permissions === 'admin' && dataDepartment.holder?.id) {
            pending = requestsList
                .filter((request) => (request.requestDetails?.sentTo === departmentName) || (request.sentTo === departmentName))
                .filter((request) => request.idProject === dataDepartment.holder?.id)
                .filter((request) => {
                    const lastHistoryItem = request.history[request.history.length - 1];
                    return lastHistoryItem?.responseRequest !== 'observed';
                });
            observedRequests = requestsList
                .filter((request) => (request.requestDetails?.sentTo === departmentName) || (request.sentTo === departmentName))
                .filter((request) => request.idProject === dataDepartment.holder?.id)
                .filter((request) => {
                    const lastHistoryItem = request.history[request.history.length - 1];
                    return lastHistoryItem?.responseRequest === 'observed' && !lastHistoryItem?.response;
                });
            respondedObservations = requestsList
                .filter((request) => (request.requestDetails?.sentTo === departmentName) || (request.sentTo === departmentName))
                .filter((request) => request.idProject === dataDepartment.holder?.id)
                .filter((request) => {
                    const lastHistoryItem = request.history[request.history.length - 1];
                    return lastHistoryItem?.responseRequest === 'observed' && lastHistoryItem?.response; // Observada y con respuesta
                });
        } else if (userData?.dimension === 'Sostenedor' || userData?.dimension === 'Establecimiento') {
            pending = requestsList
                .filter((request) => (request.requestDetails?.sentTo === departmentName) || (request.sentTo === departmentName))
                .filter((request) => request.idProject === userData.tenantId)
                .filter((request) => {
                    const lastHistoryItem = request.history[request.history.length - 1];
                    return lastHistoryItem?.responseRequest !== 'observed';
                });
            observedRequests = requestsList
                .filter((request) => (request.requestDetails?.sentTo === departmentName) || (request.sentTo === departmentName))
                .filter((request) => request.idProject === userData.tenantId)
                .filter((request) => {
                    const lastHistoryItem = request.history[request.history.length - 1];
                    return lastHistoryItem?.responseRequest === 'observed' && !lastHistoryItem?.response;
                });
            respondedObservations = requestsList
                .filter((request) => (request.requestDetails?.sentTo === departmentName) || (request.sentTo === departmentName))
                .filter((request) => request.idProject === userData.tenantId)
                .filter((request) => {
                    const lastHistoryItem = request.history[request.history.length - 1];
                    return lastHistoryItem?.responseRequest === 'observed' && lastHistoryItem?.response; // Observada y con respuesta
                });
        }
        setPendingRequests(pending);
        setObservedRequests(observedRequests);
        setRespondedObservations(respondedObservations);
    };

    const canViewPendingRequests = () => {
        return (
            (userData?.permissions === 'admin' && dataDepartment.holder.id) ||
            (userData?.dimension === 'Sostenedor' && userData?.department === departmentName)
        );
    };

    const handleSelect = (selectedOption, actionMeta) => {
        if (actionMeta.name === 'region') {
            // Si cambia la región, resetear comuna y holder
            setDataDepartment(prevData => ({ 
                ...prevData, 
                region: selectedOption.value,
                commune: null,
                holder: { value: null, id: null }
            }));
        } else if (actionMeta.name === 'commune') {
            // Si cambia la comuna, solo resetear holder
            setDataDepartment(prevData => ({ 
                ...prevData, 
                commune: selectedOption.value,
                holder: { value: null, id: null }
            }));
        } else if (actionMeta.name === 'holder') {
            // Si selecciona holder, actualizar solo ese valor
            setDataDepartment(prevData => ({
                ...prevData,
                holder: {
                    value: selectedOption.value,
                    id: selectedOption.id
                }
            }));
        }
    };

    const handleClick = (e) => {
        e.preventDefault();
        setView(true);
        setViewObserved(false);
        setViewResponseObservation(false);
    };

    const handleClickObserved = (e) => {
        e.preventDefault();
        setViewObserved(true);
        setView(false);
        setViewResponseObservation(false);
    };

    const handleClickResponseObs = (e) => {
        e.preventDefault();
        setViewResponseObservation(true);
        setView(false);
        setViewObserved(false);
    };

    const DropdownIndicator = (props) => {
        return (
            <components.DropdownIndicator {...props}>
                <img src={downArrow} alt="CustomArrow" />
            </components.DropdownIndicator>
        );
    };

    return (
        <section className={`container-department flex ${(view || viewObserved || viewResponseObservation) ? 'pending-request-open' : ''}`}>
            <header className='header-department flex'>
                <h3>{(userData && userData.dimension === 'Sostenedor') ? dataProjects.find(project => project.id === userData.tenantId).nameProject : dataDepartment.holder?.value}</h3>
                <img src={arrow} className='icon' alt='Icono de flecha' />
                <p>{getFullDepartmentName(departmentName)}</p>
            </header>
            {userData?.permissions === 'admin' && (
                <article className='container-search box-input flex'>
                    <span className='input-select'>
                        <label htmlFor='region' className='hidden-label'>Región</label>
                        <Select
                            name='region'
                            id='region'
                            value={dataDepartment.region ? { value: dataDepartment.region, label: dataDepartment.region } : null}
                            options={staticData.regiones.map((region) => ({
                                value: region.name,
                                label: region.name
                            }))
                                .sort((a, b) => a.label.localeCompare(b.label))}
                            placeholder='Selecciona Región'
                            className='custom-select'
                            classNamePrefix='custom-select'
                            components={{ DropdownIndicator }}
                            onChange={handleSelect}
                        />
                    </span>
                    <span className='input-select'>
                        <label htmlFor='commune' className='hidden-label'>Comuna</label>
                        <Select
                            name='commune'
                            id='commune'
                            value={dataDepartment.commune ? { value: dataDepartment.commune, label: dataDepartment.commune } : null}
                            options={dataDepartment.region ? staticData.regiones.find(region => region.name === dataDepartment.region)?.communes.map(commune => ({
                                value: commune.name,
                                label: commune.name
                            }))
                                .sort((a, b) => a.label.localeCompare(b.label)) : []}
                            placeholder='Selecciona Comuna'
                            className='custom-select'
                            classNamePrefix='custom-select'
                            components={{ DropdownIndicator }}
                            onChange={handleSelect}
                        />
                    </span>
                    <span className='input-select'>
                        <label htmlFor='projects' className='hidden-label'>Proyecto</label>
                        <Select
                            name='holder'
                            id='holder'
                            value={dataDepartment.holder.value ? { value: dataDepartment.holder.value, label: dataDepartment.holder.value, id: dataDepartment.holder.id } : null}
                            options={dataDepartment.commune ? dataProjects.filter((project) => project.commune === dataDepartment.commune).map((holder) => ({
                                value: holder.nameProject,
                                label: holder.nameProject,
                                id: holder.id
                            }))
                                .sort((a, b) => a.label.localeCompare(b.label)) : []}
                            placeholder='Selecciona Proyecto'
                            className='custom-select'
                            classNamePrefix='custom-select'
                            components={{ DropdownIndicator }}
                            onChange={handleSelect}
                        />
                    </span>
                </article>
            )}
            {((userData?.permissions === 'admin' && dataDepartment.holder.id) || (userData?.dimension === 'Sostenedor') || (userData?.dimension === 'Establecimiento')) && (
                <>
                    <article className='box-request-pending flex'>
                        <button
                            className={`box flex ${!canViewPendingRequests() ? 'disabled' : ''}`}
                            onClick={handleClick}
                            disabled={!canViewPendingRequests()}
                        >
                            <p>Este departamento tiene</p>
                            <span className='flex'>
                                <p className='number'>{pendingRequests.length}</p>
                            </span>
                            <p>Solicitudes pendientes</p>
                        </button>
                        {observedRequests.length > 0 && (
                            <button
                                className={`box flex ${!canViewPendingRequests() ? 'disabled' : ''}`}
                                onClick={handleClickObserved}
                                disabled={!canViewPendingRequests()}
                            >
                                <p>Este departamento tiene</p>
                                <span className='flex'>
                                    <p className='number'>{observedRequests.length}</p>
                                </span>
                                <p>Solicitudes observadas</p>
                            </button>
                        )}
                        {respondedObservations.length > 0 && (
                            <button
                                className={`box flex ${!canViewPendingRequests() ? 'disabled' : ''}`}
                                onClick={handleClickResponseObs}
                                disabled={!canViewPendingRequests()}
                            >
                                <p>Este departamento tiene</p>
                                <span className='flex'>
                                    <p className='number'>{respondedObservations.length}</p>
                                </span>
                                <p>Observaciones respondidas</p>
                            </button>
                        )}
                    </article>

                    <article className='users-department flex'>
                        <p>Funcionarios {departmentName}</p>
                        <table>
                            <thead>
                                <tr>
                                    <th>Nombre Funcionario</th>
                                    <th>Contacto</th>
                                    <th>Correo</th>
                                    <th>Estado</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredUsers.map((user) => (
                                    <tr key={user.id}>
                                        <td data-label='Nombre Funcionario'>{formatDisplayName(user.displayName)}</td>
                                        <td data-label='Contacto'>{user.phoneNumber ? user.phoneNumber : 'S/N'}</td>
                                        <td data-label='Correo'>{formatDisplayName(user.email)}</td>
                                        <td data-label='Estado'>{user.state}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </article>
                </>
            )}
            {(view || viewObserved || viewResponseObservation) && (
                <ViewPendingRequests
                    setView={view ? setView : (viewObserved ? setViewObserved : setViewResponseObservation)}
                    pendingRequests={view ? pendingRequests : (viewObserved ? observedRequests : respondedObservations)}
                    userData={userData}
                    establishmentList={establishmentList}
                    pmeList={pmeList}
                    projectId={userData?.permissions === 'admin' ? dataDepartment.holder?.id : userData?.tenantId}
                    requestObserved={location.state?.requestObserved || viewObserved}
                    answerObservation={location.state?.answerObservation || viewResponseObservation}
                    data={dataDepartment}
                    selectedRequest={selectedRequest}
                    setSelectedRequest={setSelectedRequest}
                    departmentName={departmentName}
                    view={view}
                    viewObserved={viewObserved}
                    viewResponseObservation={viewResponseObservation}
                    currentRequestType={currentRequestType}
                    setCurrentRequestType={setCurrentRequestType}
                />
            )}
        </section>
    );
};

export default Department;